<template>
  <v-form class="enterprise-form">
    <div class="form-intro">
      <p class="intro-eyebrow">Enterprise inquiry</p>
      <p class="intro-title">Tell us what you need in under 2 minutes.</p>
      <p class="intro-subtitle">
        We reply within 1 business day. No commitment required.
      </p>
    </div>

    <p class="section-label">Contact details</p>
    <v-row dense>
      <v-col cols="12" md="6">
        <v-text-field
          :value="value.name"
          label="Full name"
          placeholder="Jane Doe"
          outlined
          dense
          required
          hide-details="auto"
          @input="updateField('name', $event)"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          :value="value.work_email"
          label="Work email"
          type="email"
          placeholder="you@hospital.com"
          outlined
          dense
          required
          hide-details="auto"
          @input="updateField('work_email', $event)"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" md="7">
        <v-text-field
          :value="value.organization"
          label="Organization"
          placeholder="Hospital, clinic, or company"
          outlined
          dense
          required
          hide-details="auto"
          @input="updateField('organization', $event)"
        />
      </v-col>
      <v-col cols="12" md="5">
        <v-text-field
          :value="value.role"
          label="Role"
          placeholder="Radiologist, CTO, IT lead..."
          outlined
          dense
          required
          hide-details="auto"
          @input="updateField('role', $event)"
        />
      </v-col>
    </v-row>

    <v-text-field
      :value="value.country"
      label="Country"
      placeholder="United States"
      outlined
      dense
      required
      hide-details="auto"
      @input="updateField('country', $event)"
    />

    <p class="section-label">Needs</p>
    <div class="choice-block">
      <p class="choice-label">Deployment:</p>
      <v-radio-group
        :value="deploymentSelection"
        class="choice-radio-group"
        row
        required
        hide-details="auto"
        @change="updateDeploymentType"
      >
        <v-radio
          v-for="option in deploymentTypeOptions"
          :key="option"
          :label="option"
          :value="option"
          class="choice-radio"
        />
      </v-radio-group>
    </div>

    <div class="choice-block">
      <p class="choice-label">Users (approx.):</p>
      <v-radio-group
        :value="value.estimated_users"
        class="choice-radio-group"
        row
        required
        hide-details="auto"
        @change="updateField('estimated_users', $event)"
      >
        <v-radio
          v-for="option in estimatedUsersOptions"
          :key="`users-${option}`"
          :label="option"
          :value="option"
          class="choice-radio"
        />
      </v-radio-group>
    </div>

    <div class="choice-block">
      <p class="choice-label">Storage (approx.):</p>
      <v-radio-group
        :value="value.estimated_storage"
        class="choice-radio-group"
        row
        required
        hide-details="auto"
        @change="updateField('estimated_storage', $event)"
      >
        <v-radio
          v-for="option in estimatedStorageOptions"
          :key="`storage-${option}`"
          :label="option"
          :value="option"
          class="choice-radio"
        />
      </v-radio-group>
    </div>

    <v-textarea
      :value="value.message"
      label="Anything else? (optional)"
      placeholder="Current tools, compliance needs, timeline, or budget range"
      outlined
      dense
      rows="2"
      auto-grow
      hide-details="auto"
      @input="updateField('message', $event)"
    />
    <p class="trust-note">
      We use this information only to prepare your demo and quote.
    </p>
  </v-form>
</template>

<script>
export default {
  name: "EnterpriseInquiryForm",
  props: {
    value: {
      type: Object,
      required: true
    },
    deploymentTypeOptions: {
      type: Array,
      default: () => []
    },
    estimatedStorageOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      estimatedUsersOptions: ["1-10", "11-25", "26-100", "101-500", "500+"]
    };
  },
  computed: {
    deploymentSelection() {
      const values = Array.isArray(this.value.deployment_type)
        ? this.value.deployment_type
        : [];
      return values[0] || "";
    }
  },
  methods: {
    updateField(field, fieldValue) {
      this.$emit("input", {
        ...this.value,
        [field]: fieldValue
      });
    },
    updateDeploymentType(selectedValue) {
      this.updateField("deployment_type", selectedValue ? [selectedValue] : []);
    }
  }
};
</script>

<style scoped>
.enterprise-form {
  display: grid;
  gap: 10px;
}

.form-intro {
  margin-bottom: 2px;
}

.intro-eyebrow {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8ab4f8;
}

.intro-title {
  margin: 4px 0 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  color: #ffffff;
}

.intro-subtitle {
  margin: 6px 0 0;
  font-size: 13px;
  color: #c9c9c9;
}

.section-label {
  margin: 4px 0 0;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #b5b5b5;
}

.choice-block {
  margin-top: 2px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.choice-label {
  margin: 7px 0 0;
  min-width: 150px;
  font-size: 13px;
  font-weight: 600;
  color: #e0e0e0;
}

.choice-radio-group {
  margin: 0;
  flex: 1;
}

::v-deep .choice-radio-group .v-input--radio-group__input {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
}

::v-deep .choice-radio-group .v-input--selection-controls__input {
  margin-right: 6px;
}

::v-deep .choice-radio {
  margin: 0;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid #3f3f3f;
  background: #2a2a2a;
}

::v-deep .choice-radio .v-label {
  color: #dcdcdc !important;
  font-size: 12px;
  font-weight: 600;
}

.trust-note {
  margin: 2px 0 0;
  font-size: 12px;
  color: #a8a8a8;
}

@media (max-width: 760px) {
  .choice-block {
    display: block;
  }

  .choice-label {
    margin: 0 0 6px;
    min-width: 0;
  }
}
</style>
